/**
 * NewTenantWizard — assistente de 3 passos para provisionar um novo tenant
 * AWS Org (TASK-2435).
 *
 * Steps:
 *   0 — Identificação (project_name, cost_center)
 *   1 — Estrutura (envs diretos + opcional SaaS com sub-tenants)
 *   2 — Revisão (sumário + botão para POST /v1/admin/tenants/provision)
 *
 * Backend: POST /api/v1/admin/tenants/provision (TASK-2434)
 * Spec Pydantic: src/consoleai/api_models/provisioning.py
 *
 * Erros tratados:
 *   - 422 Pydantic / 400 ValueError → mostra detail no toast
 *   - 503 feature flag desligada → toast "Recurso indisponível"
 *   - 429 rate limit → toast "Muitas requisições"
 *   - outros → toast genérico com message
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stepper,
  Button,
  Group,
  Container,
  Title,
  Paper,
} from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react'
import {
  awsOrgProvisioningApi,
  PROJECT_NAME_REGEX,
  type TenantSpec,
} from '@/api/awsOrgProvisioning'
import { WizardStepIdentificacao } from './wizard/WizardStepIdentificacao'
import { WizardStepEstrutura } from './wizard/WizardStepEstrutura'
import { WizardStepRevisao } from './wizard/WizardStepRevisao'

const TOTAL_STEPS = 3

const EMPTY_SPEC: TenantSpec = {
  project_name: '',
  direct_environments: [],
  is_saas: false,
  sub_tenants: [],
  cost_center: null,
  tags_extra: {},
}

export function NewTenantWizard() {
  const navigate = useNavigate()
  const [active, setActive] = useState(0)
  const [spec, setSpec] = useState<TenantSpec>(EMPTY_SPEC)

  const stepValid = useMemo(() => isStepValid(active, spec), [active, spec])

  const provisionMutation = useMutation({
    mutationFn: () => awsOrgProvisioningApi.provision(spec),
    onSuccess: (data) => {
      notifications.show({
        color: 'teal',
        title: 'Provisionamento iniciado',
        message: `Job #${data.job_id} criado. Acompanhe o progresso na tela de jobs.`,
      })
      navigate(`/admin/tenants/jobs/${data.job_id}`)
    },
    onError: (err: any) => {
      const status = err?.response?.status
      const detail = err?.response?.data?.detail

      let title = 'Falha ao iniciar provisionamento'
      let message = typeof detail === 'string' ? detail : String(err)

      if (status === 503) {
        title = 'Recurso indisponível'
        message =
          'O provisioning v2 está desativado neste ambiente (feature flag).'
      } else if (status === 429) {
        title = 'Muitas requisições'
        message = 'Aguarde alguns segundos e tente novamente.'
      } else if (status === 422 && Array.isArray(detail)) {
        // FastAPI validation errors → join field paths
        message = detail
          .map((d: any) => `${(d.loc ?? []).join('.')}: ${d.msg}`)
          .join('\n')
      } else if (status === 403) {
        title = 'Sem permissão'
        message =
          'Seu usuário não tem a permissão aws_tenants:provision necessária.'
      }

      notifications.show({
        color: 'red',
        title,
        message,
      })
    },
  })

  const handleNext = () => {
    if (!stepValid) return
    setActive((a) => Math.min(a + 1, TOTAL_STEPS - 1))
  }
  const handleBack = () => setActive((a) => Math.max(a - 1, 0))
  const handleCancel = () => navigate('/admin/tenants')

  return (
    <Container size="md" py="lg">
      <Title order={2} mb="lg">
        Novo tenant
      </Title>

      <Paper withBorder p="lg" radius="md">
        <Stepper
          active={active}
          onStepClick={(i) => {
            // Permite navegar para passos anteriores ou para o atual; bloqueia
            // pular adiante sem validação.
            if (i < active) setActive(i)
            else if (i > active && isStepValid(active, spec)) setActive(i)
          }}
          allowNextStepsSelect={false}
        >
          <Stepper.Step
            label="Identificação"
            description="Nome do projeto"
            data-testid="wizard-step-identificacao"
          >
            <WizardStepIdentificacao spec={spec} onChange={setSpec} />
          </Stepper.Step>

          <Stepper.Step
            label="Estrutura"
            description="Ambientes e SaaS"
            data-testid="wizard-step-estrutura"
          >
            <WizardStepEstrutura spec={spec} onChange={setSpec} />
          </Stepper.Step>

          <Stepper.Step
            label="Revisão"
            description="Confirmar e iniciar"
            data-testid="wizard-step-revisao"
          >
            <WizardStepRevisao
              spec={spec}
              onConfirm={() => provisionMutation.mutate()}
              loading={provisionMutation.isPending}
            />
          </Stepper.Step>
        </Stepper>

        <Group justify="space-between" mt="xl">
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={14} />}
            onClick={active === 0 ? handleCancel : handleBack}
            disabled={provisionMutation.isPending}
          >
            {active === 0 ? 'Cancelar' : 'Voltar'}
          </Button>

          {active < TOTAL_STEPS - 1 && (
            <Button
              rightSection={<IconArrowRight size={14} />}
              onClick={handleNext}
              disabled={!stepValid}
              data-testid="wizard-next"
            >
              Próximo
            </Button>
          )}
        </Group>
      </Paper>
    </Container>
  )
}

/** Valida se o passo `step` está OK para avançar (espelha regras Pydantic). */
export function isStepValid(step: number, spec: TenantSpec): boolean {
  if (step === 0) {
    return PROJECT_NAME_REGEX.test(spec.project_name)
  }
  if (step === 1) {
    if (spec.is_saas) {
      // SaaS: precisa de >=1 sub-tenant válido com >=1 ambiente cada
      if (spec.sub_tenants.length === 0) return false
      return spec.sub_tenants.every(
        (st) =>
          PROJECT_NAME_REGEX.test(st.name) && st.environments.length > 0,
      )
    }
    // Não-SaaS: precisa de >=1 ambiente direto
    return spec.direct_environments.length > 0
  }
  return true
}
